import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Combobox, type ComboboxOption } from '@/components/combobox';
import { DatePickerField } from '@/components/date-picker-field';

import type { ActiveDealRow } from '../api/use-active-deals';
import { isKnownPartner, useAssignPartnerForm } from '../hooks/use-assign-partner-form';
import { FINANCING_PARTNERS, type FinancingPartner } from '../lib/financing-partners';

interface AssignFinancingPartnerDialogProps {
  row: ActiveDealRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PARTNER_OPTIONS: readonly ComboboxOption[] = FINANCING_PARTNERS.map((p) => ({
  value: p,
  label: p,
}));

export function AssignFinancingPartnerDialog({
  row,
  open,
  onOpenChange,
}: Readonly<AssignFinancingPartnerDialogProps>) {
  const form = useAssignPartnerForm(row, open, onOpenChange);

  function handleOpenChange(next: boolean) {
    if (form.isPending) return;
    onOpenChange(next);
  }

  if (row === null) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assign-financing-partner-dialog">
        <DialogHeader>
          <DialogTitle>Assign financing partner</DialogTitle>
          <DialogDescription>
            {row.bl_order_name ?? 'BL Order'}
            {row.buyer_name === null ? '' : ` — ${row.buyer_name}`}
          </DialogDescription>
        </DialogHeader>

        <AssignPartnerBody form={form} />

        <AssignPartnerFooter
          form={form}
          onCancel={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function AssignPartnerBody({ form }: Readonly<{ form: ReturnType<typeof useAssignPartnerForm> }>) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label
          htmlFor="assign-partner-combobox"
          className="text-muted-foreground text-xs uppercase tracking-wide"
        >
          Financing partner
        </Label>
        <Combobox
          id="assign-partner-combobox"
          options={PARTNER_OPTIONS}
          value={form.partner ?? undefined}
          onChange={(next) => {
            form.setPartner(isKnownPartner(next ?? null) ? (next as FinancingPartner) : null);
          }}
          placeholder="Select partner…"
          searchable={false}
          clearable
          disabled={form.isPending}
        />
      </div>

      <DatePickerField
        id="assign-sold-date"
        label="Sold date"
        value={form.soldDate}
        onChange={form.setSoldDate}
        disabled={form.isPending}
      />

      {form.errorMessage !== null && (
        <p className="text-destructive text-sm" data-testid="assign-financing-partner-error">
          {form.errorMessage}
        </p>
      )}
    </div>
  );
}

function AssignPartnerFooter({
  form,
  onCancel,
}: Readonly<{ form: ReturnType<typeof useAssignPartnerForm>; onCancel: () => void }>) {
  return (
    <DialogFooter className="sm:justify-between">
      <Button
        type="button"
        variant="outline"
        disabled={form.isPending}
        onClick={form.handleClear}
        data-testid="assign-financing-partner-clear"
      >
        Clear
      </Button>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button type="button" variant="ghost" disabled={form.isPending} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={form.isPending}
          onClick={form.handleSave}
          data-testid="assign-financing-partner-save"
        >
          {form.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
          Save
        </Button>
      </div>
    </DialogFooter>
  );
}
